// Awizacja import — Excel parser.
//
// Reads a Peute `iooo_awizacja*.xlsx`: Tab.1 = one row per shipment
// (header-mapped), Tab.2 = the Annex VII carrier chain in merged sections
// 5(a)/5(b)/5(c) (fixed column offsets), joined by the Peute reference.
// Uses `exceljs` (dynamically imported — same convention as exportToExcel.ts).

import type { RawAwizacjaRow, RawCarrierLeg, Means, CarrierRole } from "./awizacjaTypes";

type CellVal = unknown;

function text(v: CellVal): string {
  if (v == null) return "";
  if (typeof v === "string") return v.replace(/\u00a0/g, " ").replace(/\u202a|\u202c/g, "").trim();
  if (typeof v === "number") return String(v);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "object") {
    const o = v as { text?: string; result?: unknown; richText?: { text: string }[]; hyperlink?: string };
    if (Array.isArray(o.richText)) return text(o.richText.map((r) => r.text).join(""));
    if (o.text != null) return text(o.text);
    if (o.result != null) return text(o.result);
  }
  // Fall-through (boolean/bigint/symbol, or an object with no text/result/richText):
  // String() is exactly the previous behaviour — the cast only tells TS the value
  // is stringifiable.
  return String(v as { toString(): string }).trim();
}

function isoDate(v: CellVal): string | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  const s = text(v);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

function normalizeMeans(v: string): Means {
  const s = v.toLowerCase();
  if (s.includes("ship") || s.includes("vessel") || s.includes("sea")) return "ship";
  if (s.includes("train") || s.includes("rail")) return "train";
  return "truck";
}

// Header → column index, by fuzzy substring match on the header row.
// Scan a bounded range — `cellCount` can report the sheet's full width.
const MAX_HEADER_COLS = 60;
function headerMap(headerRow: { getCell: (c: number) => { value: CellVal } }): (needle: string) => number {
  const map: Record<string, number> = {};
  for (let c = 1; c <= MAX_HEADER_COLS; c++) {
    const h = text(headerRow.getCell(c).value).toLowerCase();
    if (h) map[h] = c;
  }
  const keys = Object.keys(map);
  return (needle: string) => {
    const n = needle.toLowerCase();
    const k = keys.find((key) => key.includes(n));
    return k ? map[k] : -1;
  };
}

// Fixed section offsets for Tab.2 (row 2 = field headers). 5(a) starts at B(2),
// 5(b) at N(14), 5(c) at W(23). Within a section: name, address, contact,
// phone, email, means-a, means-b, means-c, [driver, reg, driver-phone], nip.
const SECTIONS: { role: CarrierRole; base: number; hasDriver: boolean }[] = [
  { role: "5a", base: 2, hasDriver: true },
  { role: "5b", base: 14, hasDriver: false },
  { role: "5c", base: 23, hasDriver: false },
];

export class AwizacjaParser {
  async parse(file: File | ArrayBuffer, fileName = "awizacja.xlsx"): Promise<RawAwizacjaRow[]> {
    const ExcelJS = await import("exceljs");
    const wb = new ExcelJS.Workbook();
    const buf = file instanceof ArrayBuffer ? file : await file.arrayBuffer();
    await wb.xlsx.load(buf);

    const t1 = wb.getWorksheet("Tab.1") ?? wb.worksheets[0];
    const t2 = wb.getWorksheet("Tab.2") ?? wb.worksheets[1];
    if (!t1) return [];

    // ── Tab.2 carrier chain, indexed by Peute ref ─────────────────────────
    const carriersByRef = new Map<string, RawCarrierLeg[]>();
    if (t2) {
      for (let r = 3; r <= t2.rowCount; r++) {
        const row = t2.getRow(r);
        const ref = text(row.getCell(1).value);
        if (!ref) continue;
        const legs: RawCarrierLeg[] = [];
        for (const s of SECTIONS) {
          const name = text(row.getCell(s.base).value);
          // Means of transport: a = TYPE (truck/ship/train); b + c = vehicle
          // number(s), combined as "b/c" when both present.
          const meansA = text(row.getCell(s.base + 5).value);
          const meansB = text(row.getCell(s.base + 6).value);
          const meansC = text(row.getCell(s.base + 7).value);
          if (!name && !meansA) continue;
          const vehicle = [meansB, meansC].filter(Boolean).join("/");
          const driver = s.hasDriver ? text(row.getCell(s.base + 8).value) || null : null;
          const reg = s.hasDriver ? text(row.getCell(s.base + 9).value) : "";
          const driverPhone = s.hasDriver ? text(row.getCell(s.base + 10).value) || null : null;
          const nipCol = s.hasDriver ? s.base + 11 : s.base + 8;
          legs.push({
            role: s.role,
            name,
            nip: text(row.getCell(nipCol).value) || null,
            driver,
            driverPhone,
            plate: (vehicle || reg) || null,
            means: normalizeMeans(meansA),
          });
        }
        carriersByRef.set(ref, legs);
      }
    }

    // ── Tab.1 shipments ───────────────────────────────────────────────────
    const h = headerMap(t1.getRow(1) as unknown as { getCell: (c: number) => { value: CellVal }; cellCount: number });
    const col = {
      ref: h("peute reference"),
      order: h("purchase document"),
      collection: h("collection date"),
      item: h("vendor item description"),
      vendor: h("vendor name"),
      country: h("departure country"),
      zip: h("departure post code"),
      city: h("departure city"),
      street: h("departure address"),
      recovery: h("recovery facility"),
      size: h("size"),
      shipowner: h("shipowner"),
      container: h("container"),
    };

    const rows: RawAwizacjaRow[] = [];
    for (let r = 2; r <= t1.rowCount; r++) {
      const row = t1.getRow(r);
      const ref = col.ref > 0 ? text(row.getCell(col.ref).value) : "";
      if (!ref) continue;
      rows.push({
        sourceFile: fileName,
        peuteRef: ref,
        orderNumber: col.order > 0 ? text(row.getCell(col.order).value) : "",
        collectionDate: col.collection > 0 ? isoDate(row.getCell(col.collection).value) : null,
        timeslot: null,
        itemDesc: col.item > 0 ? text(row.getCell(col.item).value) : "",
        vendorName: col.vendor > 0 ? text(row.getCell(col.vendor).value) : "",
        depCountry: col.country > 0 ? text(row.getCell(col.country).value) : "",
        depZip: col.zip > 0 ? text(row.getCell(col.zip).value) : "",
        depCity: col.city > 0 ? text(row.getCell(col.city).value) : "",
        depStreet: col.street > 0 ? text(row.getCell(col.street).value) : "",
        recoveryFacility: col.recovery > 0 ? text(row.getCell(col.recovery).value) : "",
        container: col.container > 0 ? text(row.getCell(col.container).value) : "",
        size: col.size > 0 ? text(row.getCell(col.size).value) : "",
        shipowner: col.shipowner > 0 ? text(row.getCell(col.shipowner).value) : "",
        carriers: carriersByRef.get(ref) ?? [],
      });
    }
    return rows;
  }
}
